import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Grid,
  Hidden,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  NumberField,
  RefreshButton,
  SelectInput,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { Pagination } from "../../../components/Pagination";
import { CustomField } from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { LowInventoryIcon } from "../../../components/common/LowInventoryIcon";
import { QuickFilter } from "../../../components/common/QuickFilter";
import { SearchField } from "../../../components/common/SearchField";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { piecesRenderer, quantityRenderer } from "../../../utils/utils";
import { getTotalQuantityAvailable } from "../../../managerApp/resources/products/ProductUtils";
import ExportDropDown from "../../../components/common/ExportDropDown";
import { getUnitName } from "../../../utils/loadTypeUtils";
import { Empty } from "../../../components/common/Empty.js";
import {
  productExportCsv,
  productExportPdf,
} from "../../../managerApp/resources/products/productExports.js";
import { InventoriesPeak } from "./InventoriesPeak.js";
import { useUser } from "../../../authenticatorApp/UserContext.js";

const useStyles = makeStyles((theme) => ({
  totals: {
    marginLeft: theme.spacing(2),
  },
  muiInput: {
    marginBottom: theme.spacing(1),
    minWidth: "13rem",
  },
}));

const ProductSearchField = (props) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;
  const { label, alwaysOn, disabled } = props;

  const handleSearch = (value) => {
    debugger;
    if (!value || disabled) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    } else if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { name: { matchPhrasePrefix: `${value}` } },
            { name: { wildcard: `*${value}*` } },
            { productCode: { matchPhrasePrefix: `${value}` } },
            { productCode: { wildcard: `*${value}*` } },
            { customFields: { matchPhrasePrefix: `${value}` } },
            { customFields: { wildcard: `*${value}*` } },
          ],
        },
        displayedFilters,
      );
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      disabled={disabled}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (appUser, account, mdDown) => {
  const commodities = appUser?.reloadYard?.activeYardCommodities?.items || [];

  return [
    <ProductSearchField label="Search" alwaysOn disabled={!account} />,
    <SelectInput
      source="reloadYardCommodityId.eq"
      label="Load Type"
      variant="outlined"
      resettable
      allowEmpty
      alwaysOn={!mdDown}
      disabled={!account}
      choices={commodities}
      optionText="commodity.name"
    />,
    <QuickFilter
      source="totalQuantityOnHand"
      label="On hand"
      defaultValue={{ gt: 0 }}
    />,
    <QuickFilter
      source="lowInventoryAlert"
      label="Low stock"
      defaultValue={{ eq: true }}
    />,
  ];
};

const ListActions = (props) => {
  const { xsDown } = props;
  const { resource, total } = useListContext();

  return !xsDown ? (
    <TopToolbar>
      <FilterButton />
      <ExportDropDown
        total={total}
        resource={resource}
        exporter={productExportCsv}
        exporterPdf={productExportPdf}
      />
      <RefreshButton />
    </TopToolbar>
  ) : null;
};

export const ProductList = (props) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const { appUser } = useUser();

  const account = useMemo(() => {
    const id = appUser?.accountId;
    return id ? { id } : null;
  }, [appUser]);

  // Don't run filters unless account is chosen
  const defaultFilter = useMemo(() => {
    if (!account?.id) return null;
    return {
      accountId: account.id,
      activeName: {
        beginsWith: {
          active: "true",
        },
      },
    };
  }, [account]);

  const defaultSearchFilter = useMemo(() => {
    if (!account?.id) return null;
    return {
      accountId: { eq: account.id },
      active: { eq: "true" },
    };
  }, [account]);

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"productByAccountByActiveByName", "search":"name"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const [defaultPerPage, setDefaultPerPage] = useState(() => {
    const sessionPerPage = Number(
      localStorage.getItem(`${props.resource}_perPage`),
    );
    return sessionPerPage || 10;
  });

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Products",
    defaultFilter: defaultFilter,
    defaultSearchFilter: defaultSearchFilter,
    defaultPerPage,
    defaultSort,
    resource: "products",
    disabled: !account?.id,
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // reset list on automatic stale list reset
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext?.filter]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Products" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(appUser, account, mdDown)}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card>
        <ProductGrid
          {...props}
          {...listContext}
          account={account}
          xsDown={xsDown}
        />
        <PaginationToolbar {...props} account={account} />
      </Card>
    </ListContextProvider>
  );
};

const PaginationToolbar = (props) => {
  const classes = useStyles();
  const listContext = useListContext(props);
  const [total, setTotal] = useState("");
  var formatter = new Intl.NumberFormat("en-US");

  useEffect(() => {
    let onHandMap = {};
    let committedMap = {};

    if (listContext?.ids?.length > 0 && listContext?.data) {
      for (const id of listContext.ids) {
        const product = listContext.data[id];
        if (!product) continue;
        const uom = product.defaultUOM;

        onHandMap[uom] =
          (onHandMap[uom] || 0) + Number(product.totalQuantityOnHand || 0);
        committedMap[uom] =
          (committedMap[uom] || 0) +
          Number(product.totalQuantityCommitted || 0);
      }
    }

    const uoms = Object.keys(onHandMap);
    let totalString = "";

    if (uoms.length > 0) {
      const parts = uoms.map((uom) => {
        const onHand = onHandMap[uom] || 0;
        const available = onHand - (committedMap[uom] || 0);
        return `${formatter.format(onHand)} ${uom} on hand, ${formatter.format(
          available,
        )} available`;
      });
      totalString = `Total: ${parts.join("; ")}`;
    }
    setTotal(totalString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext]);

  return (
    <Grid
      container
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="flex-end"
    >
      <Hidden mdDown>
        <Grid item lg={5}>
          <Typography className={classes.totals}>
            {" "}
            {total && `${total}`}{" "}
          </Typography>
        </Grid>
      </Hidden>
      <Grid item xs={12} lg={7}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const uomRenderer = (record) => {
  const inventoryUOM = record?.defaultUOM;
  return getUnitName(inventoryUOM);
};

const ProductGrid = ({ ids, page, ...props }) => {
  const { account, xsDown } = props;
  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={false}
          />
        );
      })
    : [];

  if (ids.length === 0 && page === 1)
    return (
      <Empty {...props} message="No products were found." hasCreate={false} />
    );

  return xsDown ? (
    <SimpleList
      primaryText={(record) => record.name}
      secondaryText={(record) =>
        quantityRenderer(record.totalQuantityOnHand, record.inventoryUOM)
      }
      tertiaryText={(record) => record.productCode}
      leftIcon={LowInventoryIcon}
      rightIcon={null}
      linkType={false}
    />
  ) : (
    <CustomizableDatagrid
      rowClick="expand"
      expand={<InventoriesPeak {...props} />}
      storageProp={`ProductList_${account?.id}_large`}
      defaultColumns={[
        "productCode",
        "name",
        "totalQuantityOnHand",
        "totalQuantityCommitted",
        "totalQuantityAvailable",
        "defaultUOM",
        "lowInventoryAlert",
      ]}
    >
      {[
        <TextField source="productCode" label="Product Code" sortable={true} />,
        <TextField
          source="name"
          label="Description"
          sortable={true}
          sortBy={`{"key":"productByAccountByActiveByName", "search":"name"}`}
        />,
        <FunctionField source="pcs" label="Pieces" render={piecesRenderer} />,
        <NumberField
          source="totalQuantityOnHand"
          label="On Hand"
          sortable={true}
        />,
        <NumberField
          source="totalQuantityCommitted"
          label="Committed"
          sortable={true}
        />,
        <FunctionField
          source="totalQuantityAvailable"
          label="Available"
          sortable={false}
          render={(record) => getTotalQuantityAvailable(record)}
        />,
        <FunctionField source="defaultUOM" label="UOM" render={uomRenderer} />,
        <LowInventoryIcon
          source="lowInventoryAlert"
          label="Low Stock"
          sortable={false}
        />,
        ...customGridFields,
      ]}
    </CustomizableDatagrid>
  );
};
